import React from "react";
import { Box, Typography, Button, CircularProgress, IconButton } from "@mui/material";
import { ArrowLeft, Atom } from "lucide-react";
import BottomSheetDialog from "./mobile-bottom-sheet";
import { useSettings } from "../../../providers/settings-provider";
import useApi from "../../../hooks/use-api";
import routes from "../../../api/routes";
import buildApiRequestConfig from "../../../api/url-builder";

interface SimulationDetailProps {
    open: boolean;
    simulationId?: string | null;
    onClose: () => void;
}

const truncate = (str: string, maxLength: number = 50) => {
    if (str.length <= maxLength) return str;
    return str.slice(0, maxLength) + '...';
};

const FieldBlock: React.FC<{ label: string; value: React.ReactNode }> = ({ label, value }) => (
    <Box>
        <Typography sx={{ fontSize: 11, fontWeight: 500, color: '#707070', textTransform: 'uppercase' }}>{label}</Typography>
        <Typography sx={{ fontSize: 13, fontWeight: 600 }}>{value}</Typography>
    </Box>
);

// Bottom-sheet phone equivalent of simulation-detail.tsx.
const SimulationDetailMobile: React.FC<SimulationDetailProps> = ({
    open,
    simulationId,
    onClose,
}) => {
    const [isLoading, setIsLoading] = React.useState(false);
    const [detailData, setDetailData] = React.useState<any>(null);
    const { formatDate, formatAmount } = useSettings();
    const [fetchDetail, setFetchDetail] = useApi();

    React.useEffect(() => {
        if (!simulationId || !open) return;
        const { url, method } = buildApiRequestConfig(routes.simulation.fetchDetailById, [{ param: 'id', value: simulationId }]);
        setFetchDetail({ url, method });
    }, [simulationId, open]);

    React.useEffect(() => {
        if (fetchDetail.data) {
            setDetailData(fetchDetail.data);
            setIsLoading(false);
        }
        if (fetchDetail.isLoading) setIsLoading(true);
    }, [fetchDetail.data, fetchDetail.isLoading]);

    return (
        <BottomSheetDialog open={open} onClose={onClose}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, px: 1, py: 1, borderBottom: '1px solid #e0e0e0' }}>
                <IconButton onClick={onClose}><ArrowLeft size={22} /></IconButton>
                <Typography sx={{ fontSize: 16, fontWeight: 700, flex: 1 }}>Simulation Detail</Typography>
                <Box sx={{ px: 1.25, py: 0.5, borderRadius: 4, background: 'var(--color-primary)', color: '#fff', fontSize: 11, fontWeight: 700, display: 'flex', alignItems: 'center', gap: 0.5 }}>
                    <Atom size={12} /> Simulated
                </Box>
            </Box>
            {isLoading ? (
                <Box display="flex" flexDirection="column" alignItems="center" justifyContent="center" flex={1} gap={2}>
                    <CircularProgress size={40} />
                    <Typography color="text.secondary">Loading simulation details...</Typography>
                </Box>
            ) : (
                <Box sx={{ flex: 1, overflowY: 'auto', p: 2, display: 'flex', flexDirection: 'column', gap: 2 }}>
                    <Box>
                        <Typography sx={{ fontSize: 13, fontWeight: 700, color: 'var(--color-primary)', mb: 1 }}>SIMULATION</Typography>
                        <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 1.5, p: 1.5, border: '1px solid #e0e0e0', borderRadius: 1, background: 'var(--color-container-background)' }}>
                            <FieldBlock label="Date" value={detailData?.simulatedTransaction ? formatDate(new Date(detailData.simulatedTransaction.date)) : '—'} />
                            <FieldBlock label="Amount" value={detailData?.simulatedTransaction ? formatAmount?.(detailData.simulatedTransaction.amount) : '—'} />
                            <Box sx={{ gridColumn: '1 / -1' }}>
                                <FieldBlock label="Description" value={truncate(detailData?.simulatedTransaction?.description ?? '', 60)} />
                            </Box>
                            <FieldBlock label="Category" value={detailData?.simulatedTransaction?.category?.name ?? '—'} />
                            <FieldBlock label="Bank" value={detailData?.simulatedTransaction?.bankAccount?.accountName ?? '—'} />
                        </Box>
                    </Box>

                    <Box>
                        <Typography sx={{ fontSize: 13, fontWeight: 700, color: 'var(--color-primary)', mb: 1 }}>RULE</Typography>
                        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1, p: 1.5, border: '1px solid #e0e0e0', borderRadius: 1, background: 'var(--color-container-background)' }}>
                            <FieldBlock label="Simulation" value={detailData?.simulation?.name ?? '—'} />
                            <FieldBlock label="Mode" value={detailData?.rule?.mode ? detailData.rule.mode.charAt(0).toUpperCase() + detailData.rule.mode.slice(1) : '—'} />
                            <FieldBlock
                                label="Details"
                                value={detailData?.rule?.details?.start && detailData?.rule?.details?.end
                                    ? `Between: ${formatDate(new Date(detailData.rule.details.start))} to ${formatDate(new Date(detailData.rule.details.end))}`
                                    : detailData?.rule?.description ?? '—'}
                            />
                            {detailData?.rule?.details?.defer && (
                                <FieldBlock label="Defer Until" value={formatDate(new Date(detailData.rule.details.defer))} />
                            )}
                        </Box>
                    </Box>

                    <Box>
                        <Typography sx={{ fontSize: 13, fontWeight: 700, color: 'var(--color-primary)', mb: 1 }}>ORIGINAL</Typography>
                        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                            {(detailData?.originalTransactions ?? []).length === 0 ? (
                                <Typography sx={{ fontSize: 13, color: 'var(--color-text-muted)' }}>No original transactions</Typography>
                            ) : (detailData?.originalTransactions ?? []).map((tx: any, idx: number) => (
                                <Box key={idx} sx={{ p: 1.25, borderRadius: 1, border: '1px solid #e0e0e0', background: 'var(--color-container-background)', display: 'flex', justifyContent: 'space-between' }}>
                                    <Box>
                                        <Typography sx={{ fontSize: 13, fontWeight: 600 }}>{formatDate(new Date(tx.date))}</Typography>
                                        <Typography sx={{ fontSize: 12, color: '#707070' }}>{tx.description}</Typography>
                                    </Box>
                                    <Typography sx={{ fontSize: 13, fontWeight: 700 }}>{formatAmount?.(tx.amount)}</Typography>
                                </Box>
                            ))}
                        </Box>
                    </Box>
                </Box>
            )}
            <Box sx={{ borderTop: '1px solid #e0e0e0', p: 1.5, position: 'sticky', bottom: 0, background: 'var(--color-page-background)' }}>
                <Button onClick={onClose} variant="outlined" fullWidth>Close</Button>
            </Box>
        </BottomSheetDialog>
    );
};

export default SimulationDetailMobile;
